import React, { ErrorInfo, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { ErrorBoundaryHoc } from "@components/boundary/ErrorBoundaryHoc";
import { Button } from "@components/buttons";
import { useRootAppStore } from "@hooks";
import { redirect } from "@state";
import { getCurrentCulture, redirectToHomePage, resetSession } from "@utils";
import { logger } from "@utils/logger/logger";

import DesktopLayout from "../layout/DesktopLayout";

export const ErrorPage = ErrorBoundaryHoc(ErrorPageContent);

function ErrorPageContent(props: {
	error: Error | string;
	errorInfo: ErrorInfo | string;
	reload: () => void;
	full?: boolean;
}) {
	const { error, errorInfo, full } = props;
	useEffect(() => {
		logger.logError(error, {
			level: "fatal",
			extra: { isErrorBoundary: true },
		});
	}, []);

	if (full) {
		return (
			<DesktopLayout>
				<section className="l--content l--content--full">
					<div className="l--content__main">
						<div className="container">
							<div className="card--primary card--shadowy card--message">
								<div className="card--message__thumb" />
								<div className="card--message__body">
									<ErrorPageBody
										error={error}
										errorInfo={errorInfo}
									/>
								</div>
							</div>
						</div>
					</div>
				</section>
			</DesktopLayout>
		);
	}

	return <ErrorPageBodyNotFull error={error} errorInfo={errorInfo} />;
}

function ErrorPageBody(props: { error: any; errorInfo: any }) {
	const { error, errorInfo } = props;
	const rootStore = useRootAppStore();

	const { t } = useTranslation();

	useEffect(() => {
		const { message, stack } = new Error(error);

		if (WEBPACK_IS_SUPPORT_CHAT_AVAILABLE) {
			rootStore.diagnosticsProvider.recordErrorMsg({
				errData: {
					message,
					stack,
				},
				timestamp: new Date().toISOString(),
			});
		}
	}, []);

	return (
		<div className="card--message__content">
			<i className="card--message__icon u-icon u-icon--xlrg u-icon--remove-circle u-icon--animated--bounce-in u-color--sent--danger" />
			<div className="card--message__title">
				{t<string>("ERROR.TITLE")}
			</div>
			<div className="u-mar--bottom--xsml u-type--left">
				{t<string>("ERROR.MESSAGE")}
			</div>
			<div className="u-type--left">
				{t<string>("ERROR.MESSAGE_DETAILS")}
			</div>
			<details
				className="u-display--none u-mar--top--xsml"
				style={{
					whiteSpace: "pre-wrap",
				}}
			>
				<div className="u-mar--bottom--xsml">
					{error && error.toString()}
				</div>
				<div>{errorInfo?.componentStack}</div>
			</details>
			<div className="card--message__footer">
				<Button
					type="button"
					atClassName="errorPageBtnHome"
					className="btn--base btn--block btn--primary"
					iconRight
					iconClassName="u-icon--sml u-icon--arrow-right u-color--core--snow"
					btnContent={
						<>
							<i className="u-icon " />
							<span>{t("ERROR.404.BACK_TO_HOME")}</span>
						</>
					}
					onClick={redirectToHomePage}
				/>
				{WEBPACK_IS_SUPPORT_CHAT_AVAILABLE && (
					<Button
						type="button"
						atClassName="errorPageBtnSupport"
						className="btn--base btn--block btn--primary--inverted btn--icon"
						btnContent={
							<>
								<i className="u-icon u-icon--med u-icon--support--alt" />
								<span className="btn__label--nowrap">
									{t("CHAT.SUPPORT.TITLE")}
								</span>
							</>
						}
						onClick={() => {
							redirect(`/${getCurrentCulture()}/support-chat`);
						}}
					/>
				)}

				<div>{t("ERROR.404.RESET_SESSION_SECTION")}</div>
				<Button
					type="button"
					atClassName="errorPageFullBtn"
					className="btn--base btn--block btn--primary--inverted btn--animate-rotate"
					iconLeft
					iconClassName="u-icon--sml u-icon--refresh"
					btnText={t("ERROR.RESET_SESSION_LABEL")}
					onClick={resetSession}
				/>
			</div>
		</div>
	);
}

function ErrorPageBodyNotFull(props: { error: any; errorInfo: any }) {
	const { t } = useTranslation();
	const { error, errorInfo } = props;

	return (
		<div className="at-errorPage card--message u-mar--top--xsml">
			<div className="card--message__thumb" />
			<div className="card--message__body">
				<div className="card--message__content">
					<i className="card--message__icon u-icon u-icon--xlrg u-icon--remove-circle u-icon--animated--bounce-in u-color--sent--danger" />
					<div className="card--message__title">
						{t<string>("ERROR.TITLE")}
					</div>
					<div className="u-mar--bottom--xsml u-type--left">
						{t<string>("ERROR.MESSAGE")}
					</div>
					<div className="u-type--left">
						{t<string>("ERROR.MESSAGE_DETAILS")}
					</div>
					<details
						className="u-display--none u-mar--top--xsml"
						style={{ whiteSpace: "pre-wrap" }}
					>
						<div className="u-mar--bottom--xsml">
							{error && error.toString()}
						</div>
						<div>{errorInfo?.componentStack}</div>
					</details>

					<div className="card--message__footer">
						<Button
							className="btn--base btn--block btn--primary--inverted btn--icon"
							atClassName="errorPageFullBtn"
							type="button"
							onClick={redirectToHomePage}
							btnContent={
								<>
									<i className="u-icon u-icon--sml u-icon--nav--home" />
									<span>{t("ERROR.404.BACK_TO_HOME")}</span>
								</>
							}
						/>
						<div className="u-mar--bottom--xsml">
							{t("ERROR.404.RESET_SESSION_SECTION")}
						</div>
						<Button
							atClassName="errorPageBtn"
							className="btn btn--base btn--block btn--primary btn--icon btn--animate-rotate"
							type="button"
							onClick={resetSession}
							btnContent={
								<>
									<i className="u-icon u-icon--sml u-icon--refresh u-color--core--snow" />
									<span>
										<span>
											{t("ERROR.RESET_SESSION_LABEL")}
										</span>
									</span>
								</>
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
